import { useState, useEffect, useContext } from 'react';
import { AuthContext, apiClient } from '../App';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { MessageSquare, Clock, AlertTriangle, Smartphone, Bell } from 'lucide-react';

/**
 * SMS Notification Preferences Component
 * 
 * THIS IS THE SINGLE SOURCE OF TRUTH for sms_opt_in control.
 * 
 * Features:
 * - Toggle SMS opt-in ON/OFF
 * - When OFF: Shows consent form to enable
 * - When ON: Shows category toggles for fine-grained control
 * 
 * Categories (when opted-in):
 * 1. Game Activity - comments, non-critical updates
 * 2. Reminders - 2-hour game reminders
 * 3. Important Updates - cancellations, admin notices
 * 
 * NOTE: In-app notifications are ALWAYS enabled.
 * These toggles only control SMS delivery.
 */
const NotificationSettings = ({ compact = false }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showEnableConsent, setShowEnableConsent] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [preferences, setPreferences] = useState({
    game_activity_sms: false,
    reminders_sms: true,
    important_sms: true,
    sms_opt_in: false,
    sms_carrier_opted_out: false,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/user/notification-preferences');
      setPreferences(res.data);
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    // Optimistic update
    const oldValue = preferences[key];
    setPreferences(prev => ({ ...prev, [key]: value }));
    
    try {
      setSaving(true);
      const res = await apiClient.post('/user/notification-preferences', {
        [key]: value,
      });
      setPreferences(res.data);
      toast.success('Preference updated');
    } catch (err) {
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: oldValue }));
      console.error('Failed to update preference:', err);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Enable SMS notifications (opt-in)
   * This is the ONLY place that can set sms_opt_in = true
   */
  const handleEnableSMS = async () => {
    if (preferences.sms_carrier_opted_out) {
      toast.error('Reply START to an OpenRuns text message to enable text messages again');
      return;
    }
    if (!consentChecked) {
      toast.error('Please check the consent box to enable SMS notifications');
      return;
    }
    
    try {
      setSaving(true);
      await apiClient.post('/user/sms-opt-in');
      // Refresh preferences from server to ensure state is in sync
      await fetchPreferences();
      setConsentChecked(false);
      setShowEnableConsent(false);
      toast.success('SMS notifications enabled');
    } catch (err) {
      console.error('Failed to enable SMS:', err);
      toast.error('Failed to enable SMS notifications');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Disable SMS notifications (opt-out)
   * This is the ONLY place that can set sms_opt_in = false
   */
  const handleDisableSMS = async () => {
    // If confirmation not shown yet, show it
    if (!showDisableConfirm) {
      setShowDisableConfirm(true);
      return;
    }
    
    // User confirmed, proceed with opt-out
    try {
      setSaving(true);
      setShowDisableConfirm(false);
      await apiClient.post('/user/sms-opt-out');
      // Refresh preferences from server to ensure state is in sync
      await fetchPreferences();
      toast.success('SMS notifications disabled');
    } catch (err) {
      console.error('Failed to disable SMS:', err);
      toast.error('Failed to disable SMS notifications');
    } finally {
      setSaving(false);
    }
  };

  const cancelDisable = () => {
    setShowDisableConfirm(false);
  };

  const handleMasterChange = (checked) => {
    if (preferences.sms_carrier_opted_out || saving) return;
    if (checked) {
      setShowEnableConsent(true);
      setShowDisableConfirm(false);
    } else {
      setShowDisableConfirm(true);
      setShowEnableConsent(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-zinc-500">
        Loading notification settings...
      </div>
    );
  }

  const categories = [
    {
      key: 'game_activity_sms',
      label: 'Game Activity',
      description: 'Comments and non-critical game updates',
      icon: MessageSquare,
      color: 'text-blue-500',
    },
    {
      key: 'reminders_sms',
      label: 'Reminders',
      description: '2-hour reminder before your games start',
      icon: Clock,
      color: 'text-amber-500',
    },
    {
      key: 'important_sms',
      label: 'Important Updates',
      description: 'Game cancellations and admin notices',
      icon: AlertTriangle,
      color: 'text-red-500',
    },
  ];

  return (
    <div className={`${compact ? '' : 'space-y-6'}`}>
      <section
        className={`${compact ? 'p-4' : 'p-6'} rounded-xl border border-zinc-200 bg-white`}
        aria-labelledby="text-message-heading"
      >
        <div className="flex items-start gap-3">
          <Smartphone className="mt-0.5 h-6 w-6 shrink-0 text-orange-500" aria-hidden="true" />
          <div>
            <h2 id="text-message-heading" className="font-semibold text-zinc-900">Text Messages</h2>
            <p className="mt-1 text-sm text-zinc-600">Push notifications handle routine updates.</p>
            <p className="mt-1 text-sm text-zinc-500">
              Text messages are best reserved for important game changes.
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4 border-t border-zinc-100 pt-4">
          <div>
            <Label htmlFor="sms-master-toggle" className="font-medium text-zinc-900">
              Receive Text Messages
            </Label>
            <p className="mt-0.5 text-sm text-zinc-500">
              {preferences.sms_opt_in ? 'ON' : 'OFF'}
            </p>
          </div>
          <Switch
            id="sms-master-toggle"
            checked={preferences.sms_opt_in}
            onCheckedChange={handleMasterChange}
            disabled={saving || preferences.sms_carrier_opted_out}
            aria-describedby={preferences.sms_carrier_opted_out ? 'sms-carrier-stop-message' : undefined}
            data-testid="toggle-sms-master"
          />
        </div>

        {preferences.sms_carrier_opted_out && (
          <div
            id="sms-carrier-stop-message"
            className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
            data-testid="sms-carrier-stop"
          >
            <p className="font-medium">This phone number has opted out of text messages.</p>
            <p className="mt-1">
              Reply START to an OpenRuns text message to enable SMS again.
            </p>
          </div>
        )}

        {!preferences.sms_opt_in && !preferences.sms_carrier_opted_out && !user?.phone_number && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-800">
              <span className="font-medium">Phone number required:</span> Please add your phone number in your
              <a href="/profile" className="ml-1 text-orange-600 hover:underline">Profile</a> first.
            </p>
          </div>
        )}

        {showEnableConsent && !preferences.sms_opt_in && !preferences.sms_carrier_opted_out && user?.phone_number && (
          <div className="mt-4 rounded-lg bg-zinc-50 p-4" data-testid="sms-enable-consent">
            <div className="flex items-start gap-3">
              <Checkbox
                id="sms_consent"
                checked={consentChecked}
                onCheckedChange={setConsentChecked}
                data-testid="sms-consent-checkbox"
                className="mt-0.5"
              />
              <Label
                htmlFor="sms_consent"
                className="cursor-pointer text-xs leading-relaxed text-zinc-600"
              >
                I agree to receive SMS text messages from Open Runs USA related to game confirmations,
                reminders, and account notifications. Message and data rates may apply. Message frequency
                may vary. Reply STOP to opt out at any time and HELP for help. Consent is not a condition
                of participation.
              </Label>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={handleEnableSMS}
                disabled={!consentChecked || saving}
                className="bg-orange-500 text-white hover:bg-orange-600"
                data-testid="enable-sms-btn"
              >
                {saving ? 'Enabling...' : 'Enable Text Messages'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEnableConsent(false);
                  setConsentChecked(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {showDisableConfirm && preferences.sms_opt_in && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="disable-confirm-dialog">
            <p className="text-sm font-medium text-red-800">
              Are you sure you want to turn off text messages?
            </p>
            <p className="mt-2 text-xs leading-5 text-red-700">
              Important alerts such as cancellations or waitlist promotions may only be delivered
              through your remaining enabled notification methods.
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDisableSMS}
                disabled={saving}
                data-testid="confirm-disable-btn"
              >
                {saving ? 'Turning off...' : 'Turn off text messages'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelDisable}
                disabled={saving}
                data-testid="cancel-disable-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Category Toggles */}
      <section className="space-y-4" aria-label="Text message categories">
        {categories.map(({ key, label, description, icon: Icon, color }) => (
          <div
            key={key}
            className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-xl hover:border-zinc-300 transition-colors"
          >
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 ${color} mt-0.5`} />
              <div>
                <Label
                  htmlFor={key}
                  className="font-medium text-zinc-900 cursor-pointer"
                >
                  {label}
                </Label>
                <p className="text-sm text-zinc-500 mt-0.5">{description}</p>
              </div>
            </div>
            <Switch
              id={key}
              checked={preferences[key]}
              onCheckedChange={(checked) => updatePreference(key, checked)}
              disabled={saving || !preferences.sms_opt_in}
              data-testid={`toggle-${key}`}
            />
          </div>
        ))}
      </section>
    </div>
  );
};

export default NotificationSettings;
